import React from 'react';

export default function MyScheduleHeader({ 
  searchTerm = '', 
  setSearchTerm = () => {},
  leftContent,
  rightContent,
  isSelectionMode = false,
  onToggleSelectionMode = () => {},
  selectedCount = 0,
  onBulkComplete = () => {},
  isBulkCompleting = false,
  title = 'Assigned Jobs',
  showSelectionToggle = true
}) {
  // Handle search input changes
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  // Handle clearing the search
  const handleClearSearch = () => {
    setSearchTerm('');
  };
  
  return (
    <div className="bg-white shadow rounded-lg p-4 mb-6">
      <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
        <div className="flex items-center space-x-4">
          <h2 className="text-lg font-medium text-gray-900">
            {title}
          </h2>
          
          {/* Add the leftContent - column toggle buttons */}
          {leftContent && !isSelectionMode && (
            <div className="hidden sm:flex">
              {leftContent}
            </div>
          )}
          
          {/* Selection mode indicator */}
          {isSelectionMode && (
            <div className="flex items-center space-x-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {selectedCount} selected
              </span>
            </div>
          )}
        </div>
        
        <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4">
          {/* Selection Mode Toggle Button */}
          {showSelectionToggle && (
            <button
              onClick={onToggleSelectionMode}
              className={`inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md transition-colors ${
                isSelectionMode
                  ? 'border-red-300 text-red-700 bg-red-50 hover:bg-red-100'
                  : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
              }`}
            >
              {isSelectionMode ? (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Cancel
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                  Select Multiple
                </>
              )}
            </button>
          )}
          
          {/* Bulk Complete Button - only show when in selection mode with items selected */}
          {isSelectionMode && selectedCount > 0 && (
            <button
              onClick={onBulkComplete}
              disabled={isBulkCompleting}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isBulkCompleting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Completing...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Complete {selectedCount} Job{selectedCount > 1 ? 's' : ''}
                </>
              )}
            </button>
          )}
          
          {/* Right content - View Completed Jobs button - hide in selection mode */}
          {rightContent && !isSelectionMode && (
            <div className="hidden sm:flex">
              {rightContent}
            </div>
          )}
          
          {/* Search input - hide in selection mode */}
          {!isSelectionMode && (
            <div className="w-full sm:w-auto">
              <div className="relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                  </svg>
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="focus:ring-sky-500 focus:border-sky-500 block w-full pl-10 pr-10 py-2 sm:text-sm border-gray-300 rounded-md"
                  placeholder="Search properties..."
                />
                {searchTerm && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button 
                      onClick={handleClearSearch}
                      className="text-gray-400 hover:text-gray-500 focus:outline-none"
                    >
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Mobile view of leftContent and rightContent - show on smaller screens, hide in selection mode */}
      {(leftContent || rightContent) && !isSelectionMode && (
        <div className="sm:hidden mt-4 flex flex-col space-y-2">
          {leftContent && (
            <div className="flex justify-center">
              {leftContent}
            </div>
          )}
          {rightContent && (
            <div className="flex justify-center">
              {rightContent}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
